import { isRealtimeResource } from './resources';

export const isSessionId = value => typeof value === 'string'
  && /^[a-f0-9]{8}-[a-f0-9]{4}-4[a-f0-9]{3}-[89ab][a-f0-9]{3}-[a-f0-9]{12}$/i.test(value);
const keys = ['schemaVersion', 'eventId', 'environment', 'source', 'resource', 'operation', 'entityId',
  'parents', 'revision', 'changedFields', 'invalidates', 'occurredAt', 'correlationId'];
const plain = value => value !== null && typeof value === 'object' && !Array.isArray(value);

const boundedString = value => typeof value === 'string' && value.length > 0 && value.length <= 128;
const recordId = value => boundedString(value) && !['LIST', '*', '0'].includes(value)
  && [...value].every(char => char.charCodeAt(0) >= 32 && char.charCodeAt(0) !== 127);
const parentKeys = new Set(['projectId', 'quoteId', 'taskId', 'zoneId', 'companyId', 'userId', 'jobNumber']);

// Full version-1 Portal contract. Record descriptors conservatively refresh
// their resource's registered owners; payloads never overwrite local drafts.
export function isDomainInvalidation(event, environment) {
  try {
    if (!['dev', 'uat', 'production'].includes(environment)) return false;
    if (!plain(event) || Object.keys(event).length !== keys.length || keys.some(k => !Object.prototype.hasOwnProperty.call(event, k))) return false;
    if (event.schemaVersion !== 1 || event.environment !== environment || !isSessionId(event.eventId)) return false;
    if (!isRealtimeResource(event.resource) || !['created', 'updated', 'deleted', 'restored', 'invalidated'].includes(event.operation)) return false;
    if (event.entityId === null ? event.operation !== 'invalidated' : !recordId(event.entityId)) return false;
    if (event.revision !== null && !boundedString(event.revision)) return false;
    if (!plain(event.parents) || Object.entries(event.parents).some(([key, value]) => !parentKeys.has(key)
      || !(key === 'jobNumber' ? boundedString(value) : recordId(value)))) return false;
    if (!Array.isArray(event.changedFields) || event.changedFields.length > 200
      || new Set(event.changedFields).size !== event.changedFields.length
      || event.changedFields.some(field => typeof field !== 'string' || !/^[A-Za-z][A-Za-z0-9]{0,127}$/.test(field))) return false;
    if (!/^[a-z][a-z0-9-]{0,63}$/.test(event.source) || typeof event.source !== 'string') return false;
    if (typeof event.correlationId !== 'string' || !/^[A-Za-z0-9._:-]{1,128}$/.test(event.correlationId)) return false;
    if (typeof event.occurredAt !== 'string' || !/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}Z$/.test(event.occurredAt)
      || new Date(event.occurredAt).toISOString() !== event.occurredAt) return false;
    if (!Array.isArray(event.invalidates) || !event.invalidates.length || event.invalidates.length > 100) return false;
    const seen = new Set();
    for (const descriptor of event.invalidates) {
      if (!plain(descriptor) || Object.keys(descriptor).length !== 2 || !isRealtimeResource(descriptor.resource)
        || (descriptor.id !== 'LIST' && !recordId(descriptor.id))) return false;
      const key = JSON.stringify([descriptor.resource, descriptor.id]);
      if (seen.has(key)) return false;
      seen.add(key);
    }
    // Count UTF-8 bytes, matching Buffer.byteLength on the backend.
    return new Blob([JSON.stringify(event)]).size <= 16384;
  } catch { return false; }
}
